<template>
  <!--
    Per-player tag chip strip. Each tag is a `bg-tag-<color>/20` pill
    that toggles the toolbar filter on click. The `+ Tag` button
    (officer-only) opens a dropdown of every tag in the guild catalog;
    selecting a tag dispatches `store.assignTag` (via the `assign` event)
    and the parent card awaits the result. The button is hidden for
    non-officers so the chip strip stays read-only.

    Each pinned chip also carries an officer-only `×` button that
    removes the tag from THIS player via the `unassign` event. It
    stops propagation so clicking the `×` doesn't ALSO toggle the
    toolbar filter, and it lives outside the chip button itself so
    keyboard users get a separate, independently-focusable target.

    Pending + error UX: while the parent card is awaiting
    `store.assignTag`, the strip keeps the `+ Tag` button disabled
    (`aria-busy="true"`) and the dropdown options inert so a second
    pick can't queue. If the awaited Promise resolves `false` (the
    mutation or refetch failed), the strip shows an inline accessible
    error message under the chips AND keeps the dropdown open so the
    officer can retry or pick another tag without re-opening the menu.
    A success clears both pending state and any prior error.
  -->
  <div class="relative flex flex-col gap-0.5">
    <div class="flex flex-wrap items-center gap-1 mt-0.5">
      <span
        v-for="t in player.tags"
        :key="t.id"
        class="inline-flex items-center"
      >
        <button
          type="button"
          data-no-drag
          class="inline-flex items-center min-h-5 px-1.5 rounded-full text-xs font-semibold tracking-[0.02em] whitespace-nowrap cursor-pointer transition-opacity duration-[120ms] hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :class="tagChipClassFor({ color: t.color, active: isActiveTagFilter(t.id) })"
          :title="messages.playerStrip.toggleTitle(tagDisplayName(t.name))"
          :aria-label="messages.playerStrip.toggleAria(tagDisplayName(t.name))"
          :aria-pressed="isActiveTagFilter(t.id) ? 'true' : 'false'"
          @click="emit('toggle', t.id)"
        >{{ tagDisplayName(t.name) }}</button>
        <button
          v-if="canEdit"
          type="button"
          data-no-drag
          class="inline-flex items-center justify-center h-6 w-6 min-h-6 min-w-6 -ml-1 rounded-full text-2xs font-semibold leading-none text-text-muted hover:text-red-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :title="messages.playerStrip.removeTitle(tagDisplayName(t.name), playerLabel)"
          :aria-label="messages.playerStrip.removeAria(tagDisplayName(t.name), playerLabel)"
          @click.stop="onUnassign(t.id)"
        >×</button>
      </span>
      <button
        v-if="canEdit && assignableTagOptions.length > 0"
        ref="triggerRef"
        type="button"
        data-no-drag
        class="inline-flex items-center min-h-5 px-1.5 rounded-full text-xs font-semibold tracking-[0.04em] uppercase text-text-secondary border border-dashed cursor-pointer transition-[color,border-color] duration-[120ms] hover:text-accent hover:border-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-text-secondary disabled:hover:border-border"
        :title="addTagTitle"
        aria-haspopup="listbox"
        :aria-expanded="open"
        :aria-controls="listboxId"
        :aria-busy="assignPending ? 'true' : 'false'"
        :disabled="assignPending"
        @click="toggleListbox"
      >{{ assignPending ? messages.playerStrip.triggerLabelPending : messages.playerStrip.triggerLabelIdle }}</button>
      <ul
        v-if="open && assignableTagOptions.length > 0"
        :id="listboxId"
        ref="listRef"
        class="absolute top-full left-3 mt-1 z-40 min-w-32 max-w-48 bg-bg-elevated border border-border-strong rounded-md shadow-lg p-1 flex flex-col gap-0.5"
        role="listbox"
        :aria-label="messages.playerStrip.dropdownLabel"
        tabindex="-1"
        @click.stop
      >
        <li
          v-for="t in assignableTagOptions"
          :key="t.id"
          :id="`${listboxId}-opt-${t.id}`"
          role="option"
          :aria-selected="false"
          :aria-disabled="assignPending ? 'true' : 'false'"
        >
          <button
            type="button"
            class="w-full text-left px-2 py-1 rounded-sm text-xs cursor-pointer hover:bg-bg-surface focus-visible:outline-2 focus-visible:outline-accent flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
            :disabled="assignPending"
            @click="onAssign(t.id)"
          >
            <span
              class="inline-block w-2 h-2 rounded-full"
              :class="tagSwatchClassFor(t.color)"
              aria-hidden="true"
            />
            <span class="truncate">{{ tagDisplayName(t.name) }}</span>
          </button>
        </li>
      </ul>
    </div>
    <!--
      Inline error surfaced when the awaited `assignTag` Promise resolves
      `false`. Lives outside the chip row so long error text wraps cleanly
      without disturbing the chip layout. `role="alert"` means screen
      readers announce the failure the moment it lands, even if focus
      is still on the dropdown.
    -->
    <p
      v-if="assignError"
      class="text-xs text-red-400 leading-snug"
      role="alert"
    >{{ assignError }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import {
  tagChipClassFor,
  tagDisplayName,
  tagSwatchClassFor,
} from '@/features/gvg-planner/composables'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useListbox } from '@/shared/composables'
import { playerDisplayName } from '../../lib/playerIdentity'
import type { Player, PlayerNameMode, PlayerTag } from '../../types'
import { playerTagsEn } from './locales/en.ts'
import { playerTagsRu } from './locales/ru.ts'

export interface PlayerTagStripProps {
  readonly player: Player
  readonly playerNameMode: PlayerNameMode
  /** Permission gate; mirrors the parent card's `canEdit`. */
  readonly canEdit: boolean
  /** Predicate the parent owns (reads from the store) so the strip stays a pure presentational surface. */
  readonly isActiveTagFilter: (tagId: string) => boolean
  /**
   * Pre-computed list of tag catalog minus already-pinned tags. The parent
   * computes this from the store to keep the strip decoupled from guild-scoped
   * data.
   */
  readonly assignableTagOptions: ReadonlyArray<PlayerTag>
  /**
   * `true` while the parent card is awaiting the `store.assignTag` Promise.
   * While pending the `+ Tag` trigger is `disabled` + `aria-busy="true"` and
   * every dropdown option is `disabled` so a second click inside the request
   * window can't queue a duplicate dispatch.
   */
  readonly assignPending: boolean
  /**
   * Inline accessible error message surfaced when the awaited `assignTag`
   * Promise resolves `false`. `null` hides the error region. The parent
   * owns the message — it forwards `store.error` (or a human-readable
   * fallback) on failure and clears it on the next dispatch attempt.
   */
  readonly assignError: string | null
}

const props = defineProps<PlayerTagStripProps>()

/**
 * Events:
 *   - `toggle` — a pinned tag was clicked; parent should flip the
 *     board-wide filter via the store.
 *   - `assign` — the user picked an option from the listbox; parent
 *     should `await store.assignTag(...)`. While awaiting, the parent
 *     passes `assignPending: true` back in via props so the strip
 *     greys out its options. On resolve, the parent passes
 *     `assignError: null | string` to clear or surface the failure.
 *   - `unassign` — the officer-only `×` button on a pinned chip was
 *     clicked; parent should call `store.unassignTag`. Stop-propagation
 *     is wired in the template so this fires without also toggling the
 *     board-wide filter.
 *
 * Picker open / close transitions are owned by the strip itself (via
 * `useListbox`); the parent never needs to react to them.
 */
const emit = defineEmits<{
  (e: 'toggle', tagId: string): void
  (e: 'assign', tagId: string): void
  (e: 'unassign', tagId: string): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: playerTagsEn, ru: playerTagsRu }))

/**
 * Stable DOM id for the listbox so the trigger's `aria-controls` and the
 * `<ul>` agree. Composed once per instance — using `Math.random` would
 * give us a stable-enough id but is no longer the canonical pattern;
 * derive from the player id instead so two cards on the same board
 * cannot collide.
 */
const listboxId = computed<string>(() => `tag-picker-${props.player.id}`)

const {
  open,
  triggerRef,
  listRef,
  toggleListbox,
  closeListbox,
} = useListbox()
// `triggerRef` and `listRef` are bound to the `<button ref="triggerRef">`
// and `<ul ref="listRef">` template elements; the composable's
// click-outside handler reads them to decide whether the click target
// is inside the listbox. Pin the locals here so TS's `noUnusedLocals`
// sees the binding rather than flagging the destructured props.
void triggerRef
void listRef

/**
 * Auto-close the dropdown once a successful assignment resolves. The
 * close is gated on a REAL completion transition — previous
 * `assignPending` was `true` AND current is `false` AND current
 * `assignError` is `null` — so:
 *
 *   - **No close on initial mount.** Vue's `watch` doesn't fire on the
 *     first render by default, and even if it did the prev-vs-current
 *     check would short-circuit (prev `assignPending` is `false` on
 *     mount).
 *   - **No close on unrelated prop / player rerenders.** The source
 *     getter only reads `assignPending` + `assignError`; reactivity
 *     from `props.player` (GraphQL subscription echoes, roster
 *     refetchs, tag filter changes on the parent card) doesn't trigger
 *     this watcher.
 *   - **No close on the `pending` going `false → true` ramp-up**, or on
 *     a parent-driven error reset while idle. Only the
 *     `true → false` completion with a clean error path counts.
 *   - **No close on a failure resolution.** When `assignError` is
 *     non-null the menu stays open so the officer can retry or pick a
 *     different tag without re-opening it.
 *
 * The explicit previous-pending ref is initialized from the current prop and
 * updated on every assignment-prop watcher run. This makes the completion edge
 * independent of unrelated player rerenders and keeps failure completion open.
 */
const previousPending = ref<boolean>(props.assignPending)

watch(
  () => [props.assignPending, props.assignError] as const,
  ([pending, error]) => {
    const completedSuccessfully = previousPending.value && !pending && error === null
    previousPending.value = pending

    if (completedSuccessfully) {
      closeListbox()
    }
  },
)

function onAssign(tagId: string): void {
  if (!props.canEdit) return
  if (props.assignPending) return
  emit('assign', tagId)
}

function onUnassign(tagId: string): void {
  if (!props.canEdit) return
  emit('unassign', tagId)
}

/**
 * Human-readable player label for the remove-button's `aria-label` /
 * `title`. The strip is purely presentational and only reads `player`,
 * so this falls back gracefully when the player hasn't filled in
 * either field yet.
 */
const playerLabel = computed<string>(() => {
  return playerDisplayName(props.player, props.playerNameMode)
})

/**
 * Context-aware tooltip for the `+ Tag` button. Mirrors the disabled
 * visual state in screen-reader / hover text so officers get the same
 * reason regardless of input modality.
 */
const addTagTitle = computed<string>(() => {
  const base = messages.value.playerStrip.addTitle(playerLabel.value)
  return props.assignPending ? messages.value.playerStrip.addPendingTitle : base
})
</script>
